import { TextSelection } from "@tiptap/pm/state";

import { matchSlashQuery } from "./slashCommands.mjs";

const SUPPORTED_BLOCKS = new Set([
  "paragraph",
  "heading",
  "orderedList",
  "bulletList",
  "taskList",
  "codeBlock",
  "blockquote",
  "horizontalRule"
]);

function finite(value) {
  return typeof value === "number" && Number.isFinite(value);
}

function copyRect(value) {
  try {
    const left = value?.left;
    const right = value?.right;
    const top = value?.top;
    const bottom = value?.bottom;
    if (![left, right, top, bottom].every(finite) || right < left || bottom < top) return null;
    return { left, right, top, bottom };
  } catch {
    return null;
  }
}

function copySurface(value) {
  try {
    const width = value?.width;
    const height = value?.height;
    if (![width, height].every(finite) || width < 0 || height < 0) return null;
    return { width, height };
  } catch {
    return null;
  }
}

function clamp(value, minimum, maximum) {
  return Math.max(minimum, Math.min(value, maximum));
}

export function computeFloatingPosition(options) {
  try {
    const anchor = copyRect(options?.anchor);
    const surface = copySurface(options?.surface);
    const viewport = copyRect(options?.viewport);
    const gap = options?.gap === undefined ? 8 : options.gap;
    if (!anchor || !surface || !viewport || !finite(gap) || gap < 0) return null;

    const desiredLeft = anchor.left + (anchor.right - anchor.left - surface.width) / 2;
    const maximumLeft = Math.max(viewport.left, viewport.right - surface.width);
    const left = clamp(desiredLeft, viewport.left, maximumLeft);

    const aboveTop = anchor.top - gap - surface.height;
    const belowTop = anchor.bottom + gap;
    const fitsAbove = aboveTop >= viewport.top;
    const fitsBelow = belowTop + surface.height <= viewport.bottom;
    const spaceAbove = anchor.top - gap - viewport.top;
    const spaceBelow = viewport.bottom - anchor.bottom - gap;
    const placement = fitsAbove
      ? "top"
      : fitsBelow || spaceBelow >= spaceAbove ? "bottom" : "top";
    const desiredTop = placement === "top" ? aboveTop : belowTop;
    const maximumTop = Math.max(viewport.top, viewport.bottom - surface.height);

    return { left, top: clamp(desiredTop, viewport.top, maximumTop), placement };
  } catch {
    return null;
  }
}

function blockRecord(node, from) {
  try {
    const type = node?.type?.name;
    const nodeSize = node?.nodeSize;
    if (!SUPPORTED_BLOCKS.has(type)
      || node?.isBlock !== true
      || !Number.isInteger(from)
      || from < 0
      || !Number.isInteger(nodeSize)
      || nodeSize < 1
      || !Number.isSafeInteger(from + nodeSize - 1)) return null;
    const block = { type, from, to: from + nodeSize - 1 };
    if (type === "heading" && Number.isInteger(node.attrs?.level)) block.level = node.attrs.level;
    return block;
  } catch {
    return null;
  }
}

export function readCurrentBlock(state) {
  try {
    const selection = state?.selection;
    if (!selection) return null;
    if (selection.node) {
      const selected = blockRecord(selection.node, selection.from);
      if (selected) return selected;
    }

    const resolved = selection.$from;
    const depth = resolved?.depth;
    if (!Number.isInteger(depth) || depth < 1) return null;
    for (let currentDepth = depth; currentDepth > 0; currentDepth -= 1) {
      const node = resolved.node(currentDepth);
      const type = node?.type?.name;
      if (!SUPPORTED_BLOCKS.has(type)) continue;
      const block = blockRecord(node, resolved.before(currentDepth));
      if (block) return block;
    }
    return null;
  } catch {
    return null;
  }
}

export function readSelection(editor) {
  try {
    const selection = editor?.state?.selection;
    if (!(selection instanceof TextSelection)) return null;
    const from = selection?.from;
    const to = selection?.to;
    if (selection?.empty === true
      || !Number.isInteger(from)
      || !Number.isInteger(to)
      || from < 0
      || to <= from) return null;
    const text = editor.state.doc.textBetween(from, to);
    return typeof text === "string" ? { from, to, text } : null;
  } catch {
    return null;
  }
}

export function readSelectionRect(editor) {
  try {
    const selection = readSelection(editor);
    if (!selection) return null;
    const start = copyRect(editor?.view?.coordsAtPos(selection.from));
    const end = copyRect(editor?.view?.coordsAtPos(selection.to));
    if (!start || !end) return null;
    const left = Math.min(start.left, end.left);
    const right = Math.max(start.right, end.right);
    const top = Math.min(start.top, end.top);
    const bottom = Math.max(start.bottom, end.bottom);
    const width = right - left;
    const height = bottom - top;
    if (!finite(width) || !finite(height)) return null;
    return { left, right, top, bottom, width, height };
  } catch {
    return null;
  }
}

export function readBlockRect(editor, block) {
  try {
    const from = block?.from;
    if (!Number.isInteger(from) || from < 0) return null;
    const node = editor?.view?.nodeDOM(from);
    const element = typeof node?.getBoundingClientRect === "function" ? node : node?.parentElement;
    if (typeof element?.getBoundingClientRect !== "function") return null;
    return copyRect(element.getBoundingClientRect());
  } catch {
    return null;
  }
}

export function readSlashContext(editor) {
  try {
    const selection = editor?.state?.selection;
    const from = selection?.from;
    const to = selection?.to;
    if (selection?.empty !== true
      || !Number.isInteger(from)
      || from !== to) return null;
    const resolved = selection.$from;
    const parent = resolved?.parent;
    if (parent?.isTextblock !== true) return null;
    const match = matchSlashQuery(parent.textContent, resolved.parentOffset, resolved.start());
    if (!match || match.to !== from) return null;
    const caretRect = copyRect(editor?.view?.coordsAtPos(from));
    return caretRect ? { ...match, caretRect } : null;
  } catch {
    return null;
  }
}

function emptySnapshot() {
  return {
    focused: false,
    editable: false,
    selection: null,
    selectionRect: null,
    block: null,
    blockRect: null,
    slash: null
  };
}

export function createEditorContextSnapshot(editor) {
  const snapshot = emptySnapshot();
  try {
    snapshot.focused = editor?.isFocused === true;
  } catch {}
  try {
    snapshot.editable = editor?.isEditable === true;
  } catch {}
  snapshot.selection = readSelection(editor);
  snapshot.selectionRect = snapshot.selection ? readSelectionRect(editor) : null;
  let state = null;
  try {
    state = editor?.state;
  } catch {}
  snapshot.block = readCurrentBlock(state);
  snapshot.blockRect = snapshot.block ? readBlockRect(editor, snapshot.block) : null;
  snapshot.slash = snapshot.editable && snapshot.focused ? readSlashContext(editor) : null;
  return snapshot;
}
